import { formatCurrency } from '@if/core/utils'
import { Eyebrow, FieldLabel, FieldValue, Icon, StrongValue } from '@if/mui'
import { Divider, Stack } from '@mui/material'
import { CalendarSimulation } from '@src/types/loan/loan-simulation'
import { ArrowRight } from 'lucide-react'
import { memo, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

interface EditPaymentsImpactProps {
  simulationBefore: CalendarSimulation
  simulationAfter: CalendarSimulation
}

interface ImpactFigure {
  key: string
  label: string
  before: string
  after: string
  changed: boolean
}

const EditPaymentsImpact = ({ simulationBefore, simulationAfter }: EditPaymentsImpactProps) => {
  const { t } = useTranslation()

  const figures = useMemo<ImpactFigure[]>(() => {
    const before = simulationBefore.summaryInformation
    const after = simulationAfter.summaryInformation

    const daysLateBefore = before?.daysLate ?? 0
    const daysLateAfter = after?.daysLate ?? 0
    const balanceBefore = before?.outstandingBalance ?? 0
    const balanceAfter = after?.outstandingBalance ?? 0

    return [
      {
        key: 'daysLate',
        label: t('loan.payment.preview.nbLateDays'),
        before: t('common.days', { count: daysLateBefore }),
        after: t('common.days', { count: daysLateAfter }),
        changed: daysLateBefore !== daysLateAfter,
      },
      {
        key: 'latePrincipal',
        label: t('loan.payment.preview.latePrincipal'),
        before: formatCurrency(balanceBefore),
        after: formatCurrency(balanceAfter),
        changed: balanceBefore !== balanceAfter,
      },
    ]
  }, [simulationAfter, simulationBefore, t])

  return (
    <Stack direction="row" spacing={2} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 1 }} useFlexGap>
      <Eyebrow>{t('loan.payment.editPayments.impact.title')}</Eyebrow>
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        divider={<Divider orientation="vertical" flexItem />}
        sx={{ flexWrap: 'wrap', rowGap: 1 }}
        useFlexGap
      >
        {figures.map((figure) => (
          <Stack key={figure.key} spacing={0.25}>
            <FieldLabel>{figure.label}</FieldLabel>
            <Stack direction="row" spacing={0.75} alignItems="center">
              {figure.changed ? (
                <>
                  <FieldValue color="text.secondary" sx={{ textDecoration: 'line-through' }}>
                    {figure.before}
                  </FieldValue>
                  <Icon icon={ArrowRight} size="xs" color="disabled" />
                  <StrongValue>{figure.after}</StrongValue>
                </>
              ) : (
                <StrongValue>{figure.after}</StrongValue>
              )}
            </Stack>
          </Stack>
        ))}
      </Stack>
    </Stack>
  )
}

export default memo(EditPaymentsImpact)
